/**
 * BackgroundGenerationReconnector
 *
 * App-level headless component that reconnects background generation services
 * after page refresh. Follows the same pattern as RunStreamInit.
 *
 * On mount, waits for generationStore hydration then checks if there are
 * active background tasks that need reconnection:
 *
 * 1. activeScriptGeneration with backgroundStreaming → reconnects BackgroundStreamService
 * 2. backgroundChats → BackgroundChatListener handles these automatically (already mounted)
 * 3. xrayPendingJobs → XRayExportPollerInit handles these automatically (already mounted)
 *
 * Rendered once in App.js alongside BackgroundChatListener, XRayExportPollerInit, RunStreamInit.
 */

import { useEffect, useRef } from 'react';
import { useGenerationStore } from '../../store/generation/generationStore';
import BackgroundStreamService from '../../features/scriptGeneration/services/BackgroundStreamService';
import { useUser } from '../../features/auth/hooks/useUser';
import logger from '../services/logging/logger';

const BackgroundGenerationReconnector = () => {
  const hasInit = useRef(false);
  const { userData } = useUser();

  useEffect(() => {
    if (hasInit.current) return;

    // Wait for Zustand persist hydration to complete
    const unsub = useGenerationStore.persist.onFinishHydration(() => {
      hasInit.current = true;
      reconnectIfNeeded();
    });

    // If already hydrated (sync storage), check immediately
    if (useGenerationStore.persist.hasHydrated()) {
      hasInit.current = true;
      reconnectIfNeeded();
    }

    function reconnectIfNeeded() {
      const { activeScriptGeneration } = useGenerationStore.getState();

      // Only reconnect if there's an active background streaming session
      if (!activeScriptGeneration?.backgroundStreaming) return;

      // Don't reconnect if BackgroundStreamService is already running
      if (BackgroundStreamService.isActive()) {
        logger.info('BackgroundGenerationReconnector: BSS already active, skipping');
        return;
      }

      // Need userData to reconnect WebSocket
      if (!userData) {
        logger.warn('BackgroundGenerationReconnector: no userData yet, deferring reconnection');
        return;
      }

      const session = activeScriptGeneration;
      const snapshot = session.workflowSnapshot || {};

      logger.info('BackgroundGenerationReconnector: reconnecting BackgroundStreamService', {
        conversationId: session.conversationId,
        currentPhase: snapshot.currentPhase || snapshot.activeGenerationType,
        selectedEndpoints: snapshot.selectedEndpoints?.length,
      });

      // Reconnect BSS with persisted workflow context
      BackgroundStreamService.start({
        conversationId: session.conversationId,
        activeGenerationType: snapshot.activeGenerationType || snapshot.currentPhase,
        existingBuffer: '',
        userData,
        selectedEndpoints: snapshot.selectedEndpoints || [],
        generatedTestConfigs: snapshot.generatedTestConfigs || [],
        currentEndpointIndex: snapshot.currentEndpointIndex || 0,
        projectInfo: session.projectData,
        isTestSuiteMode: session.isTestSuiteMode || false,
        existingProjectId: session.existingProjectId || null,
        existingProjectName: session.existingProjectName || null,
        customFolderNames: snapshot.customFolderNames || {},
      });
    }

    return () => {
      if (typeof unsub === 'function') unsub();
    };
  }, [userData]);

  return null; // Renders nothing — side-effect only
};

export default BackgroundGenerationReconnector;
